import { useState, useEffect, useCallback, useRef } from 'react';
import { ensureAccessTokenFresh } from '@/features/auth/utils/ensureAccessTokenFresh';

interface UseSessionTimeoutReturn {
  isWarning: boolean;
  countdown: number;
  resetTimer: () => void;
}

export function useSessionTimeout(
  idleTimeoutMs: number,
  warningDurationMs: number,
  onTimeout: () => void
): UseSessionTimeoutReturn {
  const [isWarning, setIsWarning] = useState(false);
  const [countdown, setCountdown] = useState(Math.floor(warningDurationMs / 1000));
  
  const lastActivityRef = useRef<number>(Date.now());
  const warningExpiryRef = useRef<number | null>(null);
  const isWarningRef = useRef(isWarning);
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    isWarningRef.current = isWarning;
  }, [isWarning]);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const checkTimeout = useCallback(() => {
    const now = Date.now();

    if (!isWarningRef.current) {
      const idleTime = now - lastActivityRef.current;
      if (idleTime >= idleTimeoutMs) {
        setIsWarning(true);
        warningExpiryRef.current = now + warningDurationMs;
        setCountdown(Math.floor(warningDurationMs / 1000));
      }
    } else if (warningExpiryRef.current) {
      const remainingMs = warningExpiryRef.current - now;
      if (remainingMs <= 0) {
        setCountdown(0);
        onTimeoutRef.current();
        // Clear expiry to prevent multiple calls
        warningExpiryRef.current = null;
      } else {
        setCountdown(Math.ceil(remainingMs / 1000));
      }
    }
  }, [idleTimeoutMs, warningDurationMs]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningExpiryRef.current = null;
    setIsWarning(false);
    setCountdown(Math.floor(warningDurationMs / 1000));
  }, [warningDurationMs]);

  useEffect(() => {
    // Check timeout every second
    const intervalId = setInterval(checkTimeout, 1000);

    // Initial check
    checkTimeout();

    // Listen to user activity to reset the timer
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    const handleActivity = () => {
      if (!isWarningRef.current) {
        resetTimer();
        void ensureAccessTokenFresh().catch(() => {});
      }
    };

    // Listen to visibility change to catch timeout when returning to tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkTimeout();
      }
    };

    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [checkTimeout, resetTimer]);

  return {
    isWarning,
    countdown,
    resetTimer
  };
}
